import { useLocation, useNavigate } from 'react-router-dom';
import { useGetIdentity } from '@refinedev/core';
import {
  BarChart3,
  Inbox, Users, Building2,
  CreditCard, Gauge,
  Activity,
  PenTool,
  BookOpen,
  Target,
  UsersRound,
  ChevronLeft, ChevronRight,
  TrendingUp,
  Calendar,
  FileEdit,
  Megaphone,
  Map,
  GraduationCap,
  DollarSign,
  RotateCcw,
  Ticket,
  Cpu,
  ShieldCheck,
} from 'lucide-react';
import { useMemo, type FC } from 'react';
import { GLOSSARY, SIDEBAR_GLOSSARY_MAP } from '@/lib/glossary';

interface NavItem {
  key: string;
  label: string;
  path: string;
  resource: string;
  icon: FC<{ className?: string }>;
}

interface NavGroup {
  key: string;
  label: string;
  resource: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    key: 'growth', label: 'Business', resource: 'growth',
    items: [
      { key: 'growth-dashboard', label: 'Dashboard', path: '/growth', resource: 'growth', icon: BarChart3 },
      { key: 'marketing-dashboard', label: 'Marketing Dashboard', path: '/growth/marketing-dashboard', resource: 'growth/marketing-dashboard', icon: TrendingUp },
      { key: 'content-factory', label: 'Content Factory', path: '/growth/content-factory', resource: 'growth', icon: PenTool },
      { key: 'sales-intelligence', label: 'Sales Intelligence', path: '/growth/sales-intelligence', resource: 'growth', icon: Target },
      { key: 'knowledge-explorer', label: 'Knowledge Explorer', path: '/growth/knowledge-explorer', resource: 'growth', icon: BookOpen },
      { key: 'content-calendar', label: 'Content Calendar', path: '/growth/content-calendar', resource: 'growth/content-calendar', icon: Calendar },
      { key: 'blog-manager', label: 'Blog Manager', path: '/growth/blog-manager', resource: 'growth/blog-manager', icon: FileEdit },
      { key: 'campaigns', label: 'Campaigns', path: '/growth/campaigns', resource: 'growth/campaigns', icon: Megaphone },
      { key: 'referral-dashboard', label: 'Referral Network', path: '/growth/referral-dashboard', resource: 'growth', icon: UsersRound },
      { key: 'roadmap', label: 'Roadmap', path: '/growth/roadmap', resource: 'growth/roadmap', icon: Map },
    ],
  },
  {
    key: 'support', label: 'Customer Success', resource: 'support',
    items: [
      { key: 'feedback-inbox', label: 'Feedback Inbox', path: '/support', resource: 'support', icon: Inbox },
      { key: 'users', label: 'Annuaire Clients', path: '/support/users', resource: 'support/users', icon: Users },
    ],
  },
  {
    key: 'ops', label: 'Opérations', resource: 'ops',
    items: [
      { key: 'ops-dashboard', label: 'Dashboard Ops', path: '/ops', resource: 'ops', icon: BarChart3 },
      { key: 'organizations', label: 'Organisations', path: '/ops/organizations', resource: 'ops/organizations', icon: Building2 },
      { key: 'subscriptions', label: 'Abonnements', path: '/ops/subscriptions', resource: 'ops/subscriptions', icon: CreditCard },
      { key: 'token-watcher', label: 'Token Watcher', path: '/ops/token-watcher', resource: 'ops/token-watcher', icon: Gauge },
      { key: 'pricing', label: 'Pricing', path: '/ops/pricing', resource: 'ops/pricing', icon: DollarSign },
      { key: 'refunds', label: 'Remboursements', path: '/ops/billing/refunds', resource: 'ops/billing/refunds', icon: RotateCcw },
      { key: 'coupons', label: 'Coupons', path: '/ops/billing/coupons', resource: 'ops/billing/coupons', icon: Ticket },
      { key: 'ai-config', label: 'Config IA', path: '/ops/ai-config', resource: 'ops/ai-config', icon: Cpu },
      { key: 'plan-gates', label: 'Plan Gates', path: '/ops/plan-gates', resource: 'ops/plan-gates', icon: ShieldCheck },
    ],
  },
  {
    key: 'tech', label: 'Ingénierie', resource: 'tech',
    items: [
      { key: 'tech-dashboard', label: 'Dashboard Tech', path: '/tech', resource: 'tech', icon: Activity },
    ],
  },
  {
    key: 'learning', label: 'Formation', resource: 'learning',
    items: [
      { key: 'learning-hub', label: 'Apprendre', path: '/learning', resource: 'learning', icon: GraduationCap },
    ],
  },
];

const ROLE_SALES_ALLOWED: Record<string, boolean> = {
  growth: true, support: true, ops: true,
  'ops/organizations': true, 'ops/subscriptions': true, 'ops/token-watcher': true,
  'ops/pricing': false, // Platform Admin uniquement
  'ops/billing/refunds': false,
  'ops/billing/coupons': false,
  'ops/ai-config': false,
  'ops/plan-gates': false,
  tech: false,
  'growth/marketing-dashboard': true,
  'growth/content-calendar': true,
  'growth/blog-manager': true,
  'growth/campaigns': true,
  'growth/roadmap': true,
  learning: true,
};

interface Identity { roles?: string[]; }

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<Identity>();

  const roles = identity?.roles ?? [];
  const isAdmin = roles.includes('ROLE_ADMIN');

  const filteredGroups = useMemo(() => {
    if (isAdmin) return NAV_GROUPS;
    return NAV_GROUPS.filter((g) => ROLE_SALES_ALLOWED[g.resource] !== false).map((g) => ({
      ...g,
      items: g.items.filter((i) => ROLE_SALES_ALLOWED[i.resource] !== false),
    }));
  }, [isAdmin]);

  const activeKey = useMemo(() => {
    for (const group of NAV_GROUPS) {
      for (const item of group.items) {
        if (location.pathname === item.path) return item.key;
      }
    }
    for (const group of NAV_GROUPS) {
      for (const item of group.items) {
        if (location.pathname.startsWith(item.path) && item.path !== '/') return item.key;
      }
    }
    return 'growth-dashboard';
  }, [location.pathname]);

  return (
    <aside
      className={`relative flex flex-col border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 transition-all duration-300 ease-in-out ${
        collapsed ? 'w-[68px]' : 'w-56'
      }`}
    >
      {/* Toggle button — bordure droite, milieu */}
      <button
        onClick={onToggle}
        className="absolute top-1/2 -translate-y-1/2 -right-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-400 dark:text-slate-400 shadow-sm hover:bg-blue-50 dark:hover:bg-slate-600 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-200"
        title={collapsed ? 'Ouvrir le menu' : 'Réduire le menu'}
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {filteredGroups.map((group) => (
          <div key={group.key} className="mb-4">
            {/* Group label */}
            {!collapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                {group.label}
              </p>
            )}
            {collapsed && <div className="mb-1 border-b border-slate-100 dark:border-slate-700 mx-2" />}

            {/* Items */}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeKey === item.key;

                return (
                  <button
                    key={item.key}
                    onClick={() => navigate(item.path)}
                    title={collapsed ? item.label : undefined}
                    className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150 ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200'
                    } ${collapsed ? 'justify-center px-0' : ''}`}
                  >
                    <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${
                      isActive ? '' : 'group-hover:scale-110 transition-transform'
                    }`} />
                    {!collapsed && (
                      <div className="min-w-0 flex-1">
                        <span className="truncate block">{item.label}</span>
                        {group.key === 'growth' && SIDEBAR_GLOSSARY_MAP[item.key] && GLOSSARY[SIDEBAR_GLOSSARY_MAP[item.key]] && (
                          <span className="block truncate text-[10px] italic text-slate-400 dark:text-slate-500 leading-tight -mt-0.5">
                            {GLOSSARY[SIDEBAR_GLOSSARY_MAP[item.key]].fr}
                          </span>
                        )}
                      </div>
                    )}
                    {!collapsed && isActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-700 px-3 py-3">
        {!collapsed ? (
          <p className="text-[10px] text-slate-400 dark:text-slate-500">Feedback Orchestrator © 2026</p>
        ) : (
          <p className="text-center text-[10px] text-slate-400 dark:text-slate-500">FC</p>
        )}
      </div>
    </aside>
  );
};
